/**
 * Skeleton form component for form loading states
 * @module components/common/SkeletonForm
 */

import React from 'react'
import { SkeletonText, SkeletonRounded } from './Skeleton'

export interface SkeletonFormProps {
  /**
   * Number of form fields to display
   */
  fields?: number

  /**
   * Use two-column layout
   */
  twoColumn?: boolean

  /**
   * Show form header
   */
  showHeader?: boolean
}

/**
 * Skeleton form component for form loading states
 *
 * Displays skeleton for form fields and action buttons
 */
export const SkeletonForm: React.FC<SkeletonFormProps> = ({
  fields = 6,
  twoColumn = true,
  showHeader = true
}) => {
  const renderField = (index: number) => (
    <div key={index} className="space-y-2">
      <SkeletonText width="40%" height={14} />
      <SkeletonRounded width="100%" height={40} />
    </div>
  )

  return (
    <div className="p-6">
      {showHeader && (
        <div className="mb-8 space-y-2">
          <SkeletonText width="30%" height={28} />
          <SkeletonText width="50%" height={16} />
        </div>
      )}

      {twoColumn ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: fields }).map((_, index) => renderField(index))}
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from({ length: fields }).map((_, index) => renderField(index))}
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-8 flex justify-end gap-4">
        <SkeletonRounded width={100} height={40} />
        <SkeletonRounded width={120} height={40} />
      </div>
    </div>
  )
}
